import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router";
import { ChevronRight, Tractor } from "lucide-react";
import { catalogueApi } from "../../api/catalogue";
import { EmptyState, LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Icon } from "../../components/ui";
import { usePageTitle } from "./usePageTitle";

/** PRD screen 2: every kind of machine, then the machines of each kind. */
export function EquipmentCategoriesRoute() {
  usePageTitle("Browse machines");
  const families = useQuery({ queryKey: ["catalogue", "machines"], queryFn: catalogueApi.machineFamilies });

  // The mobile home's "Find your machine" tiles deep-link here with a #family-<id> anchor
  // (KALI-48). Client-side navigation and the async list mean the browser won't scroll on its
  // own, so once the list is in, land on the named family's section.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || !families.isSuccess) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, families.isSuccess]);

  return (
    <PageWidth className="pt-8">
      <h1 className="mb-2 type-h1 [font-size:var(--text-2xl)]">Browse machines</h1>
      <p className="mb-8 max-w-[40rem] type-body text-text-muted">Pick your machine to see its diagrams. Each diagram numbers every part, matching the manufacturer's book.</p>
      {families.isPending ? (
        <p className="type-body text-text-muted">Loading machines…</p>
      ) : families.isError ? (
        <LoadError onRetry={() => families.refetch()} />
      ) : families.data.length === 0 ? (
        <EmptyState icon={Tractor} title="No machines yet">
          Diagrams are being added. Check back soon.
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-10">
          {families.data.map((family) => (
            <section key={family.id} id={`family-${family.id}`} className="scroll-mt-24">
              <h2 className="mb-3 type-h3">{family.name}</h2>
              <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
                {family.equipment_models.map((machine) => (
                  <li key={machine.id}>
                    <Link
                      to={`/equipment-models/${machine.id}`}
                      className="flex min-h-16 items-center gap-4 rounded-md border border-border-subtle bg-surface-card px-5 py-3 shadow-xs hover:border-green-700"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block type-h4 [font-size:var(--text-base)] text-text-strong">{machine.name}</span>
                        <span className="block type-small text-text-muted">
                          {machine.assembly_count} {machine.assembly_count === 1 ? "diagram" : "diagrams"}
                        </span>
                      </span>
                      <Icon icon={ChevronRight} size={18} className="text-ink-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </PageWidth>
  );
}
